if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });

              // Trigger animation only on successful cart add
              if (window.cartAnimationInstance && typeof window.cartAnimationInstance.startAnimation === 'function') {
                try {
                  const productImage = this.getProductImage();
                  window.cartAnimationInstance.startAnimation(this.submitButton, productImage);
                } catch (e) {
                  // Animation error shouldn't break cart functionality
                }
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getProductImage() {
        // Cache the image URL to avoid repeated DOM searches
        if (this._cachedImageUrl !== undefined) {
          return this._cachedImageUrl;
        }

        // Try multiple strategies to find the correct product image
        let imageUrl = null;

        // Strategy 1: Look for product card wrapper around the form
        const productCard = this.closest('.card-wrapper, .product-card-wrapper, .product, .product-item, .grid__item, product-info');

        if (productCard && !imageUrl) {
          // Look for the main product image in the card
          const cardMedia = productCard.querySelector('.card__media, .product__media');
          if (cardMedia) {
            const img = cardMedia.querySelector('img:not(.secondary-image)');
            if (img && img.src && !img.src.includes('placeholder')) {
              imageUrl = img.src;
            }
          }

          // Fallback: any image in the product card
          if (!imageUrl) {
            const anyImg = productCard.querySelector('img:not(.secondary-image)');
            if (anyImg && anyImg.src && !anyImg.src.includes('placeholder')) {
              imageUrl = anyImg.src;
            }
          }
        }

        // Strategy 2: Look for product ID in form (only if not found yet)
        if (!imageUrl) {
          const productIdInput = this.form.querySelector('input[name="id"], .product-variant-id');
          if (productIdInput && productIdInput.value) {
            // Try to find image by product ID
            const productElement = document.querySelector(`[data-product-id="${productIdInput.value}"], [data-product*="${productIdInput.value}"]`);
            if (productElement) {
              const img = productElement.querySelector('img');
              if (img && img.src && !img.src.includes('placeholder')) {
                imageUrl = img.src;
              }
            }
          }
        }

        // Strategy 3: Look in the same section as the form (only if not found yet)
        if (!imageUrl) {
          const section = this.closest('[data-section-id], section, .shopify-section');
          if (section) {
            const img = section.querySelector('.product__media img, .card__media img, .media img');
            if (img && img.src && !img.src.includes('placeholder')) {
              imageUrl = img.src;
            }
          }
        }

        // Strategy 4: For product pages, look for main product image (only if not found yet)
        if (!imageUrl) {
          const mainProductImg = document.querySelector('.product__media--main img, .product-single__media img, .product__media img');
          if (mainProductImg && mainProductImg.src && !mainProductImg.src.includes('placeholder')) {
            imageUrl = mainProductImg.src;
          }
        }

        // Cache the result (even if null) to avoid repeated searches
        this._cachedImageUrl = imageUrl;
        return imageUrl;
      }
    }
  );
}